import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';
import {
  UserCircle, LayoutDashboard, FileText, Calendar, LogOut, Menu, X
} from 'lucide-react';
import './PatientDashboard.css';

function PatientDashboard() {
  const navigate = useNavigate();
  const { logout, currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [patientInfo, setPatientInfo] = useState(null);

  useEffect(() => {
    if (currentUser) fetchPatientInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  async function fetchPatientInfo() {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', currentUser.id)
        .single();
      if (error) throw error;
      setPatientInfo(data);
    } catch (error) {
      toast.error('Failed to load patient profile');
    }
  }

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  if (!patientInfo && currentUser) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--gray-50)' }}>
        Loading Patient Portal...
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="patient-layout">
        <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {mobileMenuOpen && (
          <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>
        )}

        <aside className={`patient-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header-info">
            <div className="patient-icon"><UserCircle size={28} color="white" /></div>
            <div className="patient-details">
              <h3>Patient Portal</h3>
              <p>{patientInfo?.name || currentUser?.email}</p>
            </div>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
            >
              <LayoutDashboard size={20} />
              <span>Dashboard</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'prescriptions' ? 'active' : ''}`}
              onClick={() => { setActiveTab('prescriptions'); setMobileMenuOpen(false); }}
            >
              <FileText size={20} />
              <span>My Prescriptions</span>
            </button>
            <button
              className={`nav-item ${activeTab === 'appointments' ? 'active' : ''}`}
              onClick={() => { setActiveTab('appointments'); setMobileMenuOpen(false); }}
            >
              <Calendar size={20} />
              <span>Appointments</span>
            </button>
          </nav>

          <div className="sidebar-footer">
            <button className="logout-btn-sidebar" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </aside>

        <main className="patient-main">
          <header className="patient-header">
            <div>
              <h1>
                {activeTab === 'overview' && `Welcome, ${patientInfo?.name || 'Patient'}!`}
                {activeTab === 'prescriptions' && 'My Prescriptions'}
                {activeTab === 'appointments' && 'My Appointments'}
              </h1>
              <p className="header-subtitle">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </header>

          <div className="patient-content">
            {activeTab === 'overview' && (
              <div className="empty-state">
                <UserCircle size={64} />
                <h3>Your Health Hub</h3>
                <p>We are integrating your full medical history, prescriptions, and appointment scheduling tools here. Stay tuned!</p>
                <button className="btn-primary outline" onClick={() => setActiveTab('prescriptions')}>
                  View Prescriptions
                </button>
              </div>
            )}
            {activeTab !== 'overview' && (
              <div className="empty-state">
                <LayoutDashboard size={64} />
                <h3>Feature Coming Soon</h3>
                <p>We are actively developing this section to provide you with the best experience.</p>
                <button className="btn-primary outline" onClick={() => setActiveTab('overview')}>
                  Return to Dashboard
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
}

export default PatientDashboard;